import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Sparkles, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from './auth/LoginForm';
import RegisterForm from './auth/RegisterForm';
import ResetForm from './auth/ResetForm';

export default function AuthModal() {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setEmail(''); setUsername(''); setPassword(''); setCode(''); setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === 'register') {
        await register(email, username, password, code.trim().toUpperCase());
        toast.success(`Bienvenue ${username} ! 🎉`);
      } else if (authMode === 'reset') {
        const u = await resetPassword(code.trim().toUpperCase(), email, password);
        toast.success(`Mot de passe réinitialisé. Salut ${u.username} ! ✨`);
      } else {
        const u = await login(email, password);
        toast.success(`Salut ${u.username} ! ✨`);
      }
      setAuthOpen(false);
      reset();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur de connexion';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isReset = authMode === 'reset';
  const isRegister = authMode === 'register';
  const title = isRegister ? 'Créer un compte' : isReset ? 'Réinitialiser le mot de passe' : 'Connexion';
  const description = isRegister
    ? "Bêta fermée — code d'invitation requis 🔐"
    : isReset
      ? 'Récupère ton compte avec ton code admin'
      : "Reprends là où tu t'étais arrêté(e)";

  return (
    <Dialog open={authOpen} onOpenChange={(o) => { setAuthOpen(o); if (!o) reset(); }}>
      <DialogContent className="glass border-white/10 max-w-md text-white">
        <DialogHeader>
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 flex items-center justify-center mb-2">
            {isReset ? <ShieldCheck size={24} className="text-white" /> : <Sparkles size={24} className="text-white" />}
          </div>
          <DialogTitle className="text-2xl font-black text-center">{title}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-center">{description}</DialogDescription>
        </DialogHeader>

        {isRegister && (
          <RegisterForm
            code={code} setCode={setCode}
            email={email} setEmail={setEmail}
            username={username} setUsername={setUsername}
            password={password} setPassword={setPassword}
            loading={loading}
            onSubmit={handleSubmit}
            onSwitchToLogin={() => setAuthMode('login')}
          />
        )}
        {isReset && (
          <ResetForm
            code={code} setCode={setCode}
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            loading={loading}
            onSubmit={handleSubmit}
            onSwitchToLogin={() => setAuthMode('login')}
          />
        )}
        {!isRegister && !isReset && (
          <LoginForm
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            loading={loading}
            onSubmit={handleSubmit}
            onSwitchToRegister={() => setAuthMode('register')}
            onSwitchToReset={() => setAuthMode('reset')}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
